import React, { useState } from "react";
import Modal from "../../modal-new";
import PropTypes from "prop-types";
import useTrackGoals from "./helpers/useTrackGoal";
import SvgIcon from "../../SvgIcon";
import { DayPicker } from "react-day-picker";
import { parseTime } from "../../../views/athlete-portal/calendar/sleep-time/SleepTime";
import AreaGraph from "../../../views/athlete-portal/calendar/body-composition/components/AreaGraph";
import BarGraph from "../../../views/athlete-portal/calendar/body-composition/components/BarGraph";
import { RPEList } from "../../../views/athlete-portal/calendar/session-rpe/RPEList";
import { round } from "lodash";
import convert from "convert-units";
import submit from "submit-form";
import moment from "moment";

const ModalTitle = {
  steps: "Daily Steps",
  sleep: "Daily Sleep",
  bodyWeight: "Bodyweight",
};

const detailsTarget = {
  steps: "targetSteps",
  sleep: "targetSleep",
  bodyWeight: "targetWeight",
};

const CustomCaption = ({ currentMonth, onPrevClick, onNextClick }) => {
  return (
    <div className="calendar-caption">
      <button type="button" onClick={onPrevClick} className="rd-back"></button>
      <span className="calendar-title">
        {moment(currentMonth).format("MMMM, YYYY")}
      </span>
      <button type="button" className="rd-next" onClick={onNextClick}></button>
    </div>
  );
};

const DailyGoalsModal = ({ locals }) => {
  const { query, activeUser, dailyStats, clientGoals } = locals;
  const { units } = activeUser;
  const { goalId } = query;
  const unit = activeUser.units === "metric" ? "Kg" : "Lbs";

  const currentGoal = clientGoals.find((goal) => goal._id === goalId);
  const goalType = currentGoal?.type;

  const [sortedTableData, setSortedTableData] = useState();
  const [sortedRecent, setSortedRecent] = useState();
  const goalTarget = currentGoal?.details[detailsTarget[goalType]];
  const startDate = currentGoal?.utcStartDate;

  const [month, setMonth] = useState(moment().toDate());

  const handlePrevClick = () => {
    const newMonth = moment(month).subtract(1, "month");
    const startDateMoment = moment(startDate, "YYYYMMDD");

    // Only update the month if the new month is after the starting date
    if (newMonth.isSameOrAfter(startDateMoment, "month")) {
      setMonth(newMonth.toDate());
    }
  };
  const handleNextClick = () => {
    setMonth(moment(month).add(1, "month").toDate());
  };

  const {
    lastDaysAchieved,
    streaks,
    averageStat,
    chartData,
    tableData,
    calendarDates,
  } = useTrackGoals({
    goalType,
    units,
    dailyStats,
    goalTarget,
    startDate,
  });

  const { currentStreak, bestStreak } = streaks;

  const sortList = (newState) => {
    setSortedTableData(newState.dateFormattedData);
    setSortedRecent(newState.sortedRecent);
  };

  return (
    <Modal
      className={`daily-goal-modal ${
        goalType === "bodyWeight" ? "bodyweight" : ""
      }`}
      title={ModalTitle[goalType]}
      toClear={["goalId"]}
    >
      <div className="body">
        {goalType !== "bodyWeight" && (
          <>
            <div className="goal-information">
              <div className="current-goal-header">
                <p>Current Goal</p>
                <div className="current-goal-info">
                  <p>
                    {goalType === "sleep"
                      ? parseTime(goalTarget)
                      : `${goalTarget} Steps`}
                  </p>
                  <button
                    data-button-type="primary"
                    onClick={() => {
                      submit("/actions/refresh?", {
                        method: "GET",
                        body: {
                          "modal[type]": "add-client-goal",
                          modalState: currentGoal.type,
                          goalId: currentGoal._id,
                        },
                      });
                    }}
                  >
                    <SvgIcon src={"/img/new-icons/edit-pencil.svg"} />
                  </button>
                </div>
              </div>
              <div className="goal-calendar">
                <DayPicker
                  month={month}
                  onMonthChange={(newMonth) =>
                    setMonth(moment(newMonth).toDate())
                  }
                  components={{
                    Caption: () => (
                      <CustomCaption
                        currentMonth={month}
                        onPrevClick={handlePrevClick}
                        onNextClick={handleNextClick}
                      />
                    ),
                  }}
                  modifiers={calendarDates.modifiers}
                  modifiersClassNames={calendarDates.modifiersClassNames}
                  showOutsideDays
                />
              </div>
              <div className="goal-streaks">
                <div className="goal-streak-row">
                  <div className="goal-streak-card">
                    <p>Current Streak</p>
                    <div className="goal-streak-info">
                      <p>{currentStreak}</p>
                      <p>days</p>
                    </div>
                  </div>
                  <div className="goal-streak-card">
                    <p>Daily Average</p>
                    <div className="goal-streak-info">
                      <p>
                        {goalType === "sleep"
                          ? parseTime(averageStat)
                          : averageStat}
                      </p>
                      <p>
                        /
                        {goalType === "sleep"
                          ? parseTime(goalTarget)
                          : goalTarget}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="goal-streak-row">
                  <div className="goal-streak-card">
                    <p>Best Streak</p>
                    <div className="goal-streak-info">
                      <p>{bestStreak}</p>
                      <p>Days</p>
                    </div>
                  </div>
                  <div className="goal-streak-card">
                    <p>Last 7 days</p>
                    <div className="goal-streak-info">
                      <p>{lastDaysAchieved}</p>
                      <p>/7 Achieved</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="separator"></div>
          </>
        )}

        <div className="goal-data">
          {goalType === "bodyWeight" && (
            <div className="current-goal-header">
              <p>Current Goal</p>
              <div className="current-goal-info">
                <p>
                  {`${
                    activeUser.units === "metric"
                      ? round(goalTarget || 0, 2)
                      : round(
                          convert(goalTarget || 0)
                            .from("kg")
                            .to("lb"),
                          2
                        )
                  } ${unit}`}
                </p>
                <button
                  data-button-type="primary"
                  onClick={() => {
                    submit("/actions/refresh?", {
                      method: "GET",
                      body: {
                        "modal[type]": "add-client-goal",
                        modalState: currentGoal.type,
                        goalId: currentGoal._id,
                      },
                    });
                  }}
                >
                  <SvgIcon src={"/img/new-icons/edit-pencil.svg"} />
                </button>
              </div>
            </div>
          )}

          <div className="goal-chart">
            {goalType !== "bodyWeight" ? (
              <BarGraph
                data={chartData}
                targetValue={goalType}
                graphId={`${goalType}-graph-daily-modal`}
                average={averageStat}
                parseFn={goalType === "sleep" ? parseTime : undefined}
              />
            ) : (
              <AreaGraph
                data={chartData}
                targetValue={goalType}
                graphId={"bodyweight-graph-profile"}
                label={"Body Weight"}
                description={"Weight"}
                units={unit}
              />
            )}
          </div>
          {tableData?.listData?.length > 0 && (
            <div className="goal-table">
              <RPEList
                dateFrom={tableData.dateFrom}
                dateTo={tableData.dateTo}
                list={sortedTableData || tableData.listData}
                columnsData={tableData.columnsData}
                sortedRecent={sortedRecent}
                updateState={sortList}
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

DailyGoalsModal.propTypes = {
  locals: PropTypes.any,
};

export default DailyGoalsModal;
